<template>
  <div class="app-payment-wrapper">
    <p class="top-up-text">
      <span class="uppercase">Lidl</span> <span lang="en">Connect</span> ist ein Angebot der Vodafone
      <abbr title="Gesellschaft mit beschränkter Haftung">GmbH</abbr>. Leistungserbringer und Vertragspartner der
      <span class="uppercase">Lidl</span>
      <span lang="en">Connect</span> Mobilfunkdienstleistungen ist ausschließlich die Vodafone
      <abbr title="Gesellschaft mit beschränkter Haftung">GmbH</abbr>.
    </p>
    <form>
      <div class="inputs-container">
        <div class="radio-group radio-flex">
          <label>
            <input type="radio" v-if="creditCardPossible" v-model="_paymentType" name="paymentType" value="creditCard" />
            <span class="custom-radio"></span>
            <span class="text-base">Kreditkarte</span>
            <img class="visa-img" src="/files/lidl/images/new-icons/svg/visa.svg" alt="" />
            <img src="/files/lidl/images/new-icons/svg/mastercard.svg" alt="" />
          </label>
          <EnterCreditCardData
            v-if="_paymentType === 'creditCard' && creditCardPossible"
            :redirect-url="creditCardRedirectUrl"
            :personal-data="paymentContext"
            :process-name="processName"
            @start="creditCardStart"
            :creditCardInformation.sync="creditCardInformation"
            :cc-frame-address="creditCardFrameAddress"
          >
            <template #txtBefore><slot name="txtCcBefore"></slot></template>
            <template #txtCcInfoBefore><slot name="txtCcInfoBefore"></slot></template>
            <template #lblCcInfoCcHolder><slot name="lblCcInfoCcHolder"></slot></template>
            <template #lblCcInfoCcNumber><slot name="lblCcInfoCcNumber"></slot></template>
            <template #lblCcInfoCcType><slot name="lblCcInfoCcType"></slot></template>
            <template #lblCcInfoCcValidty><slot name="lblCcInfoCcValidty"></slot></template>
            <template #txtCcInfoAfter><slot name="txtCcInfoAfter"></slot></template>
            <template #txtCcInfoError><slot name="txtCcInfoError"></slot></template>
            <template #btnCcRestart><slot name="btnCcRestart"></slot></template>
            <template #txtAfter><slot name="txtCcAfter"></slot></template>
          </EnterCreditCardData>
          <label>
            <input type="radio" v-if="bankAccountPossible" v-model="_paymentType" name="paymentType" value="bankAccount" />
            <span class="custom-radio"></span>
            <span class="text-base">Lastschrift</span>
          </label>
          <label v-if="noPaymentPossible">
            <input type="radio" v-model="_paymentType" name="paymentType" value="withoutPayment" />
            <span class="custom-radio"></span>
            <span class="text-base">Keine Zahldaten</span>
          </label>
        </div>
        <EnterBankAccountData
          v-if="_paymentType === 'bankAccount' && bankAccountPossible"
          :iban.sync="bankAccount.iban"
          :owner.sync="bankAccount.owner"
          :paymentConfirmation.sync="bankAccount.paymentConfirmation"
          :invalid.sync="bankAccountInvalid"
          :isPUC="isPUC()"
          :customer="customerInfo"
          ref="bankAccountComponent"
        >
          <template #lblOwner><slot name="lblBankAccountOwner"></slot></template>
          <template #txtIbanLiveFeedback><slot name="txtBankAccountIbanLiveFeedback"></slot></template>
          <template #lblIban><slot name="lblBankAccountIban"></slot></template>
          <template #lblPaymentConfirmation><slot name="lblBankAccountPaymentConfirmation"></slot></template>
        </EnterBankAccountData>
        <p v-show="noPaymentSelected && _paymentType === 'withoutPayment'" class="text-error">
          Bitte wähle eine Bezahloption aus
        </p>
      </div>
    </form>
  </div>
</template>

<script>
import EnterBankAccountData from './EnterBankAccountData.vue'
import EnterCreditCardData from './EnterCreditCardData.vue'
import { mapGetters } from 'vuex'
import syncProperty from '@/mixins/syncProperty'
import { validationMixin } from 'vuelidate'
import preFilledInput from '@/mixins/preFilledInput'
import { required } from 'vuelidate/lib/validators'

export default {
  name: 'EnterPaymentData',
  data() {
    return {
      paymentType: null,
      bankAccount: {
        iban: null,
        owner: null,
        paymentConfirmation: null
      },
      bankAccountInvalid: true,
      noPaymentSelected: false
    }
  },
  mounted() {
    // wenn wir über einen kreditkarten-Callback aufgerufen wurden, müssen wir die CreditCard Komponente auswählen:
    const urlParameters = new URLSearchParams(window.location.search)
    if (urlParameters.has('id') && urlParameters.has('resourcePath')) {
      this.paymentType = 'creditCard'
    } else if (this.preSelectedPayment) {
      this.applyPreSelectedPayment()
    } else if (this.token !== null && this.ccStep === 'completed') {
      // wenn es ein credit Card token im store gibt, wählen wir Kreditkarte vor
      this.paymentType = 'creditCard'
    }
  },
  components: { EnterCreditCardData, EnterBankAccountData },
  computed: {
    ...mapGetters('activation/tariff', ['savedTariff']),
    ...mapGetters(['brand']),
    ...mapGetters('payment/creditCard', {
      token: 'token',
      ccStep: 'step',
      creditCard: 'creditCard'
    }),
    _paymentType: {
      get: function() {
        return this.paymentType || 'withoutPayment'
      },
      set: function(value) {
        this.paymentType = value
      }
    },
    selectedPayment: function() {
      if (this.ccSelected) {
        return {
          paymentType: 'creditCard',
          ...this.creditCardInformation
        }
      }
      if (this.withOutPayment) {
        return {
          paymentType: 'withoutPayment'
        }
      }
      return {
        paymentType: 'bankAccount',
        ...this.bankAccount
      }
    },
    customerInfo: function() {
      if (typeof this.paymentContext !== 'undefined') {
        return this.paymentContext
      }
      return this.currentCustomer
    },
    ccSelected: function() {
      return this._paymentType === 'creditCard'
    },
    bankSelected: function() {
      return this._paymentType === 'bankAccount'
    },
    withOutPayment: function() {
      return this._paymentType === 'withoutPayment'
    },
    ccValidState: function() {
      if (this.ccStep !== 'completed') {
        return null
      }
      if (this.ccSelected === false) {
        return null
      }
      if (this.creditCardInformation === null) {
        return false
      }
      return this.creditCardInformation.registrationId !== null
    }
  },
  watch: {
    selectedPayment: {
      immediate: true,
      handler: function(value) {
        this.$emit('update:payment', value)
      }
    },
    '$v.$invalid': function(newValue) {
      this.$emit('update:paymentInvalid', newValue)
    }
  },
  methods: {
    isPUC() {
      return this.savedTariff?.tariffExtra?.market === 'PUC'
    },
    creditCardStart() {
      this.creditCardInformation = null
    },
    applyPreSelectedPayment() {
      const payment = Object.assign({}, this.preSelectedPayment)
      this.paymentType = payment.paymentType
      delete payment.paymentType
      if (this.paymentType === 'creditCard') {
        this.creditCardInformation = payment
      } else if (this.paymentType === 'bankAccount') {
        this.bankAccount = payment
      }
    }
  },
  validations() {
    const paymentValidator = params => {
      if (this.withOutPayment) {
        return true
      }
      if (params.ccSelected) {
        if (params.creditCardInformation === null) {
          return false
        }
        return params.creditCardInformation.registrationId !== null
      }
      if (params.bankSelected) {
        return params.bankAccountInvalid === false
      }
      return false
    }
    return {
      payment: paymentValidator
    }
  },
  props: {
    creditCardFrameAddress: {
      type: String
    },
    creditCardRedirectUrl: {
      type: String
    },
    paymentInvalid: {
      type: Boolean
    },
    paymentContext: {
      type: Object
    },
    bankAccountPossible: {
      type: Boolean,
      default: true
    },
    creditCardPossible: {
      type: Boolean,
      default: true
    },
    noPaymentPossible: {
      type: Boolean,
      default: false
    },
    processName: {
      type: String,
      required
    },
    currentCustomer: {
      type: Object
    },
    displayConfirmationError: {
      type: Boolean
    }
  },
  mixins: [
    validationMixin,
    syncProperty('payment', 'preSelectedPayment'),
    preFilledInput('creditCardInformation', 'creditCard')
  ]
}
</script>
